var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '../icon';
import { IconButton } from '../icon-button';
import { Select } from '../select';
import { StackLayout, StackLayoutItem } from '../stack-layout';
import { styled } from '../styled';
import { useMergedClasses } from '../styles/classes';
import { Text } from '../text';
import clsx from '../utils/clsx';
import { paginationComponentName, paginationClasses, getPaginationUtilityClass, } from './pagination-classes';
var Root = styled('fieldset', { name: paginationComponentName, slot: 'Root' })(function (_a) {
    var ownerState = _a.ownerState;
    return (__assign({ justifyContent: 'space-between', display: 'flex', alignItems: 'center', margin: 0, padding: 0, border: 'none' }, (ownerState.disabled && {
        opacity: 0.4,
        cursor: 'not-allowed',
        pointerEvents: 'none',
    })));
});
var PageSelect = styled(Select, { name: paginationComponentName, slot: 'PageSelect' })(function (_a) {
    var theme = _a.theme, value = _a.value;
    var page = Number(value) || 0;
    var charWidth = 8;
    var baseWidth = 50;
    var pageAsString = page.toString();
    var pageCharLength = pageAsString.length;
    return {
        width: theme.typography.pxToRem(pageCharLength * charWidth + baseWidth),
    };
});
var pagesFromItems = function (items, perPage) {
    return Math.ceil(items / perPage);
};
var changePageIfAppropriate = function (_a) {
    var nextPage = _a.nextPage, total = _a.total, perPage = _a.perPage, onPageChanged = _a.onPageChanged, _b = _a.ignoreNextPageCheck, ignoreNextPageCheck = _b === void 0 ? false : _b;
    var pageCount = pagesFromItems(total, perPage);
    var nextPageExists = nextPage > 0 && nextPage <= pageCount;
    var shouldCallNextPageHandler = ignoreNextPageCheck || nextPageExists;
    if (shouldCallNextPageHandler && typeof onPageChanged === 'function') {
        onPageChanged(nextPage);
    }
};
export var Pagination = React.forwardRef(function (props, ref) {
    var children = props.children, className = props.className, page = props.page, perPage = props.perPage, items = props.items, onPageChanged = props.onPageChanged, disabled = props.disabled, propClasses = props.classes, rootProps = __rest(props, ["children", "className", "page", "perPage", "items", "onPageChanged", "disabled", "classes"]);
    var classes = useMergedClasses(paginationClasses, getPaginationUtilityClass, propClasses);
    var pages = pagesFromItems(items, perPage);
    var pageOptions = React.useMemo(function () {
        return Array.from({ length: pages }).map(function (_item, idx) {
            var value = idx + 1;
            var option = {
                value: value,
                text: "".concat(value),
            };
            return option;
        });
    }, [pages]);
    // Do not show the pagination toolbar if there is one page or less
    if (pages <= 1) {
        return null;
    }
    var inFirstPage = page === 1;
    var inLastPage = page === pages;
    return (React.createElement(Root, __assign({ ref: ref, ownerState: { disabled: disabled }, className: clsx(classes.root, className) }, rootProps), items > 0 && (React.createElement(React.Fragment, null,
        React.createElement(StackLayout, { gutter: 1 },
            React.createElement(StackLayoutItem, null,
                React.createElement(Text, { className: classes.pageLabelText, variant: "body2" }, "Page")),
            React.createElement(StackLayoutItem, null,
                React.createElement(PageSelect, { size: "small", options: pageOptions, className: classes.pageSelect, value: page, onChange: function (_a) {
                        var value = _a.target.value;
                        return changePageIfAppropriate({
                            nextPage: parseInt(value, 0),
                            total: items,
                            perPage: perPage,
                            onPageChanged: onPageChanged,
                        });
                    } })),
            React.createElement(StackLayoutItem, null,
                React.createElement(Text, { className: classes.pageLabelText, variant: "body2" },
                    "of ",
                    pages))),
        React.createElement(StackLayout, { gutter: 1 },
            React.createElement(StackLayoutItem, null,
                React.createElement(IconButton, { variant: "outlined", className: classes.previousPageButton, onClick: function () { return changePageIfAppropriate({ nextPage: page - 1, total: items, perPage: perPage, onPageChanged: onPageChanged }); }, disabled: inFirstPage, "aria-label": "Previous page" },
                    React.createElement(ChevronLeftIcon, null))),
            React.createElement(StackLayoutItem, null,
                React.createElement(IconButton, { variant: "outlined", className: classes.nextPageButton, onClick: function () { return changePageIfAppropriate({ nextPage: page + 1, total: items, perPage: perPage, onPageChanged: onPageChanged }); }, disabled: inLastPage, "aria-label": "Next page" },
                    React.createElement(ChevronRightIcon, null))))))));
});
